import React, { useEffect, useState } from 'react';
import { Typography, theme } from 'antd';
import { motion } from 'framer-motion';
import { IconClock, IconClockExclamation } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';
import { TEAL, TEAL_SOFT_BG, CORAL, CORAL_SOFT_BG } from '../theme/creamyTokens';

const { Text } = Typography;

/**
 * When this screen thinks the window runs out, in this browser's own clock.
 *
 * Anchored to the server's remaining-seconds whenever it sent one: that figure
 * was measured against the same clock QuoteExpiryService sweeps on, so a
 * countdown built from it reaches zero when the server agrees it does. The
 * timestamp is the fallback for a booking fetched from an older API, and it is
 * strictly worse -- it can be misread if it arrives without a zone, and it
 * drifts with a wrong device clock. Between the two that is the whole reason
 * this screen once announced an expiry the backend had not reached, and then
 * promised a cancellation that never came.
 *
 * Computed once per booking rather than per tick: re-reading it every second
 * would just re-add Date.now() to a number Date.now() was already subtracted
 * from.
 */
const resolveDeadline = (deadlineUtc: string, remainingSeconds?: number): number =>
  remainingSeconds == null ? new Date(deadlineUtc).getTime() : Date.now() + remainingSeconds * 1000;

/**
 * Whose clock this is, which is the only thing that changes about the
 * sentence: the same deadline reads "they have X left" to the side waiting
 * and "you have X left" to the side that owes the move.
 */
export type QuoteCountdownVariant = 'theirs' | 'yours' | 'pay' | 'theirPay';

const LABEL_KEYS: Record<QuoteCountdownVariant, string> = {
  theirs: TRANSLATIONS.QUOTE_COUNTDOWN_LABEL_THEIRS,
  yours: TRANSLATIONS.QUOTE_COUNTDOWN_LABEL_YOURS,
  pay: TRANSLATIONS.QUOTE_COUNTDOWN_LABEL_PAY,
  // The same pay-by deadline read from the other side of the deal: the host
  // watching their machine held off the market until the guest settles.
  theirPay: TRANSLATIONS.QUOTE_COUNTDOWN_LABEL_THEIR_PAY,
};

const EXPIRED_KEYS: Record<QuoteCountdownVariant, string> = {
  theirs: TRANSLATIONS.QUOTE_COUNTDOWN_EXPIRED,
  yours: TRANSLATIONS.QUOTE_COUNTDOWN_EXPIRED,
  pay: TRANSLATIONS.QUOTE_COUNTDOWN_EXPIRED_PAY,
  theirPay: TRANSLATIONS.QUOTE_COUNTDOWN_EXPIRED_PAY,
};

/** Under this much left, the whole card switches to the warm palette and starts breathing. */
const URGENT_THRESHOLD_MS = 60 * 60 * 1000;

interface QuoteCountdownProps {
  /** Booking.quoteRespondByUtc or .paymentRequiredByUtc -- both are server-set from the host's own configured windows. */
  deadlineUtc: string;
  /** The matching quoteRespondInSeconds / paymentRequiredInSeconds. Preferred over deadlineUtc -- see resolveDeadline. */
  remainingSeconds?: number;
  variant?: QuoteCountdownVariant;
}

/**
 * Hours, minutes and seconds as three separate numbers, so each can have its
 * own tile.
 *
 * Hours are NOT rolled up into days: a host who set "2 days" is really
 * granting 48 hours, and that is the unit both sides reason about while a
 * machine sits off the market. "1 d 23 h" makes the reader do arithmetic to
 * get back to the number they actually chose.
 */
const splitRemaining = (ms: number) => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  return {
    hours: Math.floor(totalSeconds / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
  };
};

/** Two digits everywhere except the hours, which can legitimately run past 99 on a two-week window. */
const pad = (value: number) => String(value).padStart(2, '0');

/**
 * How long is left, as a thing you can't miss.
 *
 * This used to be one line of 12px secondary text ticking once a minute, which
 * is the typographic weight of a footnote for what is in fact a hard deadline:
 * past it the booking is cancelled and the machine goes back on the market.
 * Now it is a card in the app's own creamy language -- teal while there is
 * room, coral and gently breathing under the last hour -- with the figure
 * split into tiles so the eye lands on the number rather than reading a
 * sentence.
 *
 * Seconds are shown at every scale, including on a 48-hour window. It looks
 * like false precision and isn't: a countdown whose digits never move reads as
 * a static label, and the whole point of this component is that somebody
 * believes the clock is running. That belief is what a minute-resolution
 * readout kept failing to earn.
 *
 * Ticking every second is affordable because the state lives here: nothing
 * outside this component re-renders with it.
 */
const QuoteCountdown: React.FC<QuoteCountdownProps> = ({ deadlineUtc, remainingSeconds, variant = 'theirs' }) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [remaining, setRemaining] = useState(() => resolveDeadline(deadlineUtc, remainingSeconds) - Date.now());

  useEffect(() => {
    const deadline = resolveDeadline(deadlineUtc, remainingSeconds);
    setRemaining(deadline - Date.now());
    // Recomputed from the absolute deadline rather than decremented, so a tab
    // that was backgrounded (and had its timers throttled) catches up on its
    // next tick instead of drifting further behind for the rest of the wait.
    const interval = setInterval(() => setRemaining(deadline - Date.now()), 1000);
    return () => clearInterval(interval);
  }, [deadlineUtc, remainingSeconds]);

  const expired = remaining <= 0;
  const urgent = !expired && remaining < URGENT_THRESHOLD_MS;
  const warm = expired || urgent;

  const accent = warm ? CORAL : TEAL;
  const background = warm ? CORAL_SOFT_BG : TEAL_SOFT_BG;
  const Icon = expired ? IconClockExclamation : IconClock;

  const { hours, minutes, seconds } = splitRemaining(remaining);
  // The hours tile earns its place only once there are hours; dropping it under
  // the hour makes the last stretch read as the shorter, tenser thing it is.
  const tiles: { key: string; value: string; unitKey: string }[] = [
    ...(hours > 0 ? [{ key: 'h', value: String(hours), unitKey: TRANSLATIONS.QUOTE_COUNTDOWN_UNIT_HOURS }] : []),
    { key: 'min', value: pad(minutes), unitKey: TRANSLATIONS.QUOTE_COUNTDOWN_UNIT_MINUTES },
    { key: 's', value: pad(seconds), unitKey: TRANSLATIONS.QUOTE_COUNTDOWN_UNIT_SECONDS },
  ];

  return (
    <motion.div
      // A slow, shallow breath rather than a blink -- enough to pull the eye
      // back to a card that has become urgent without turning the thread into
      // something that flashes at you while you read it.
      animate={urgent ? { opacity: [1, 0.82, 1] } : { opacity: 1 }}
      transition={urgent ? { duration: 2.2, repeat: Infinity, ease: 'easeInOut' } : { duration: 0.2 }}
      style={{
        background,
        border: `1px solid ${accent}`,
        borderRadius: 14,
        padding: '10px 12px',
        display: 'flex',
        flexDirection: 'column',
        gap: expired ? 2 : 8,
      }}
    >
      <Text
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: accent,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          letterSpacing: 0.2,
        }}
      >
        <Icon size={14} />
        {translate(t, expired ? EXPIRED_KEYS[variant] : LABEL_KEYS[variant])}
      </Text>

      {!expired && (
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6 }}>
          {tiles.map((tile) => (
            <div
              key={tile.key}
              style={{
                background: token.colorBgContainer,
                borderRadius: 10,
                padding: '4px 9px',
                minWidth: 44,
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                lineHeight: 1.1,
              }}
            >
              <Text
                strong
                style={{
                  fontSize: 19,
                  color: accent,
                  // Digits that change every second must not resize the tile
                  // as they go -- otherwise the whole row jitters once a tick.
                  fontVariantNumeric: 'tabular-nums',
                  fontFeatureSettings: '"tnum"',
                }}
              >
                {tile.value}
              </Text>
              <Text type="secondary" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                {translate(t, tile.unitKey)}
              </Text>
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
};

export default QuoteCountdown;
